import React from 'react';
import {
  FaServer,
  FaSync,
  FaExclamationTriangle,
  FaQuestionCircle,
} from 'react-icons/fa';
import { Button, StatusDot } from './Primitives';
import { formatDateTime, formatRelativeAge } from '../utils/format';

/**
 * Deriva lo stato di connessione host (colore + etichetta) in modo difensivo.
 * Un valore non determinato non viene tradotto in "guasto".
 */
function connectionView(summary, error) {
  if (error?.kind === 'unreachable') {
    return { dot: 'bg-red-500', label: 'Non raggiungibile' };
  }
  if (error?.kind === 'not_configured') {
    return { dot: 'bg-gray-400', label: 'Non configurato' };
  }
  const state = String(summary?.connectionState || '').toLowerCase();
  if (state === 'connected') return { dot: 'bg-emerald-500', label: 'Connesso' };
  if (state === 'disconnected')
    return { dot: 'bg-red-500', label: 'Disconnesso' };
  if (state === 'notresponding')
    return { dot: 'bg-amber-500', label: 'Non risponde' };
  if (summary?.reachable === true)
    return { dot: 'bg-emerald-500', label: 'Raggiungibile' };
  if (summary?.reachable === false)
    return { dot: 'bg-red-500', label: 'Non raggiungibile' };
  return { dot: 'bg-gray-300', label: 'Non determinato' };
}

/**
 * Intestazione della pagina "Ambienti di test": titolo, sottotitolo, stato
 * connessione host, ultimo aggiornamento, badge stale e pulsante Aggiorna.
 */
export default function EsxiHeader({
  summary,
  error,
  stale,
  lastUpdated,
  refreshing,
  onRefresh,
  onHelp,
}) {
  const conn = connectionView(summary, error);
  return (
    <header className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-primary/10 via-white to-secondary/10 p-6 shadow-sm dark:border-gray-700 dark:from-primary/20 dark:via-gray-800 dark:to-gray-800">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-md">
            <FaServer size={22} aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Ambienti di test
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Controllo delle macchine virtuali dell’host ESXi 1: stato,
              monitoraggio, accensione e spegnimento controllato.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-3 py-1.5 text-xs font-medium backdrop-blur dark:border-gray-600 dark:bg-gray-900/50"
            aria-live="polite"
          >
            <StatusDot dotClass={conn.dot} label={<span className="text-gray-600 dark:text-gray-300">Host: {conn.label}</span>} />
          </div>

          {stale && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
              title="I dati mostrati potrebbero non essere aggiornati"
            >
              <FaExclamationTriangle size={11} aria-hidden="true" />
              Dati non aggiornati
            </span>
          )}

          <div className="text-right text-xs text-gray-500 dark:text-gray-400">
            <div>Ultimo aggiornamento</div>
            <div
              className="font-medium text-gray-700 dark:text-gray-200"
              title={formatDateTime(lastUpdated)}
            >
              {lastUpdated ? formatRelativeAge(lastUpdated) : '—'}
            </div>
          </div>

          <Button
            variant="secondary"
            icon={FaQuestionCircle}
            onClick={onHelp}
            aria-label="Apri la guida della dashboard"
            title="Come leggere e usare la dashboard"
          >
            Guida
          </Button>

          <Button
            variant="primary"
            icon={FaSync}
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Aggiorna dati ESXi"
            title="Ricarica inventario e stato host"
            className={refreshing ? '[&>svg]:animate-spin motion-reduce:[&>svg]:animate-none' : ''}
          >
            Aggiorna
          </Button>
        </div>
      </div>
    </header>
  );
}
